import React, { useState, useEffect } from "react"
import {X, Plus, Trash2, Edit2, Mail, User, Shield} from 'lucide-react'
import { lumi } from "../lib/lumi"

interface TeamMember {
  _id: string
  name: string
  email: string
  role: string
  active: boolean
  createdAt: string
}

interface TeamMembersManagerProps {
  onClose: () => void
}

export function TeamMembersManager({ onClose }: TeamMembersManagerProps) {
  const [members, setMembers] = useState<TeamMember[]>([])
  const [loading, setLoading] = useState(true)
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'Conseiller',
    active: true
  })

  useEffect(() => {
    loadMembers()
  }, [])

  const loadMembers = async () => {
    setLoading(true)
    try {
      const result = await lumi.entities.team_members.list({
        sort: { name: 1 }
      })
      setMembers(result.list)
    } catch (error) {
      console.error('Erreur lors du chargement des membres:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      if (editingMember) {
        await lumi.entities.team_members.update(editingMember._id, {
          ...formData,
          updatedAt: new Date().toISOString()
        })
      } else {
        await lumi.entities.team_members.create({
          ...formData,
          createdAt: new Date().toISOString()
        })
      }
      
      setFormData({ name: '', email: '', role: 'Conseiller', active: true })
      setEditingMember(null)
      setShowForm(false)
      await loadMembers()
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    }
  }

  const handleEdit = (member: TeamMember) => {
    setEditingMember(member)
    setFormData({
      name: member.name,
      email: member.email,
      role: member.role,
      active: member.active
    })
    setShowForm(true)
  }

  const handleDelete = async (memberId: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce membre ?')) return
    
    try {
      await lumi.entities.team_members.delete(memberId)
      await loadMembers()
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
    }
  }

  const cancelEdit = () => {
    setEditingMember(null)
    setFormData({ name: '', email: '', role: 'Conseiller', active: true })
    setShowForm(false)
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Gestion de l'équipe</h2>
            <p className="text-teal-100 text-sm mt-1">Gérer les membres de l'équipe pour l'assignation des dossiers</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Bouton d'ajout */}
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="mb-6 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Ajouter un membre
            </button>
          )}

          {/* Formulaire */}
          {showForm && (
            <div className="mb-6 bg-gray-50 border-2 border-teal-200 rounded-xl p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingMember ? 'Modifier le membre' : 'Nouveau membre'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                    placeholder="Jean Dupont"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                    placeholder="jean.dupont@rugir.ch"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rôle *
                  </label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  >
                    <option value="Conseiller">Conseiller</option>
                    <option value="Manager">Manager</option>
                    <option value="Admin">Admin</option>
                    <option value="Coach">Coach</option>
                  </select>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="active" className="text-sm font-medium text-gray-700">
                    Membre actif (peut être assigné aux dossiers)
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium"
                  >
                    {editingMember ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Liste des membres */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-teal-600 border-t-transparent"></div>
              <p className="text-gray-600 mt-4">Chargement...</p>
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-12">
              <User className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Aucun membre dans l'équipe</p>
              <p className="text-gray-500 text-sm mt-2">Ajoutez votre premier membre pour commencer</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {members.map((member) => (
                <div
                  key={member._id}
                  className={`bg-white border-2 rounded-xl p-5 transition-all ${
                    member.active
                      ? 'border-gray-200 hover:border-teal-300'
                      : 'border-gray-100 bg-gray-50 opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`p-3 rounded-full ${
                        member.active ? 'bg-teal-100' : 'bg-gray-200'
                      }`}>
                        <User className={`w-6 h-6 ${
                          member.active ? 'text-teal-600' : 'text-gray-400'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            member.active
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {member.active ? 'Actif' : 'Inactif'}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Mail className="w-4 h-4" />
                            <a href={`mailto:${member.email}`} className="text-sm hover:text-teal-600">
                              {member.email}
                            </a>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Shield className="w-4 h-4" />
                            <span className="text-sm font-medium">{member.role}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(member)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Modifier"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(member._id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
